import type {
  SuggestedAddress,
  ValidateAddressInput,
  ValidateAddressResponse,
  ValidateAddressUserType,
} from '../../../core/models/address-validate';
import type { ShipmentAddressesFormValue } from '../models/address-form.model';
import { buildSuggestedAddressFromTypedEntry } from './build-suggested-address-from-typed-entry';
import { formatSuggestedAddressLine } from './format-suggested-address-line';
import { hasActionableSuggestedAddress } from './has-actionable-suggested-address';
import { mapShipmentAddressesToValidateRequest } from './map-shipment-addresses-to-validate-request';
import { suggestedAddressMatchesTypedEntry } from './suggested-address-matches-typed-entry';

export type AddressSuggestionSectionKey = 'shipFrom' | 'shipTo' | 'returnAddress';

/** AVS correction panel: real suggestion vs invalid-without-suggestion warning (OM-513). */
export type AddressSuggestionMode = 'suggestion' | 'unverifiedWarning';

/** Presentation model for the suggested-address panel on a section. */
export interface AddressSuggestionPresentation {
  mode: AddressSuggestionMode;
  displayLine: string;
  suggestedAddress: SuggestedAddress;
}

const USER_TYPE_TO_SECTION: Record<ValidateAddressUserType, AddressSuggestionSectionKey> = {
  sender: 'shipFrom',
  recipient: 'shipTo',
  returnTo: 'returnAddress',
};

/**
 * Resolves the address shown on the suggestion panel for an invalid AVS result.
 * Actionable AVS suggestions win; otherwise backfill the typed entry (OM-513).
 */
function resolveSuggestedAddressForInvalidResult(
  suggestedAddress: SuggestedAddress | null | undefined,
  typedEntry: ValidateAddressInput | undefined,
): SuggestedAddress | null {
  if (hasActionableSuggestedAddress(suggestedAddress)) {
    return suggestedAddress!;
  }

  if (!typedEntry) {
    return null;
  }

  return buildSuggestedAddressFromTypedEntry(typedEntry);
}

/**
 * Maps POST /addresses/validate results to section-scoped suggestion panels.
 * Skips AVS suggestions that exactly match what the user already typed (case-insensitive).
 * Invalid results with no actionable suggestion use mode `unverifiedWarning` so Keep My Entry
 * can continue with a surcharge warning (OM-513 / ESN Ignore parity).
 */
export function mapValidateResultsToSuggestions(
  response: ValidateAddressResponse,
  formValue: ShipmentAddressesFormValue,
): Partial<Record<AddressSuggestionSectionKey, AddressSuggestionPresentation>> {
  const suggestions: Partial<Record<AddressSuggestionSectionKey, AddressSuggestionPresentation>> =
    {};
  const requestByUserType = new Map(
    mapShipmentAddressesToValidateRequest(formValue).addresses.map((entry) => [
      entry.userType,
      entry,
    ]),
  );

  for (const result of response.addressValidationResults) {
    if (result.isValid) {
      continue;
    }

    const typedEntry = requestByUserType.get(result.userType);
    const hasAvsActionableSuggestion = hasActionableSuggestedAddress(result.suggestedAddress);
    const resolvedSuggestion = resolveSuggestedAddressForInvalidResult(
      result.suggestedAddress,
      typedEntry,
    );

    if (!resolvedSuggestion) {
      continue;
    }

    // Only skip exact-match for real AVS suggestions — never for typed backfill (OM-513).
    if (
      hasAvsActionableSuggestion &&
      typedEntry &&
      suggestedAddressMatchesTypedEntry(typedEntry, resolvedSuggestion)
    ) {
      continue;
    }

    const sectionKey = USER_TYPE_TO_SECTION[result.userType];
    suggestions[sectionKey] = {
      mode: hasAvsActionableSuggestion ? 'suggestion' : 'unverifiedWarning',
      displayLine: formatSuggestedAddressLine(resolvedSuggestion),
      suggestedAddress: resolvedSuggestion,
    };
  }

  return suggestions;
}
